import React from "react";
import {
  Sparkles, Linkedin, Mail, ArrowUpRight,
  Rocket, Target, Search, Pen, Code2, Users
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTeam } from "../../hooks/useTeam";
import Reveal from "../Reveal";

// Role → icon + gradient mapping
const ROLE_ICON_MAP = [
  { match: ["founder", "kurucu", "direktör"],          Icon: Rocket,  gradient: "from-brand-pink to-brand-magenta",  bg: "bg-brand-pink/10",  color: "text-brand-pink" },
  { match: ["meta ads", "performance", "performans"],  Icon: Target,  gradient: "from-orange-500 to-rose-500",        bg: "bg-orange-50",      color: "text-orange-500" },
  { match: ["seo", "içerik", "content"],               Icon: Search,  gradient: "from-emerald-500 to-teal-500",       bg: "bg-emerald-50",     color: "text-emerald-600" },
  { match: ["grafik", "tasarım", "design", "creative"], Icon: Pen,    gradient: "from-purple-500 to-pink-500",        bg: "bg-purple-50",      color: "text-purple-600" },
  { match: ["web", "geliştir", "developer", "kod"],    Icon: Code2,   gradient: "from-blue-500 to-cyan-500",          bg: "bg-blue-50",        color: "text-blue-600" },
];

function getRoleIcon(role = "") {
  const lower = role.toLowerCase();
  const match = ROLE_ICON_MAP.find(r => r.match.some(k => lower.includes(k)));
  return match || { Icon: Users, gradient: "from-ink to-ink/70", bg: "bg-mist", color: "text-ink/60" };
}

// Hardcoded team members
const STATIC_TEAM_MEMBERS = [
  { name: "Elif Demir",      role: "Meta Ads Team Lead" },
  { name: "Mert Karaca",     role: "SEO & İçerik Uzmanı" },
  { name: "Sofia Lindström", role: "Grafik Tasarımcı" },
  { name: "Daniel Ruiz",     role: "Web Geliştirici" },
];

function isFounder(m) {
  const name = (m?.name || "").toLowerCase();
  return name.includes("ümit") || name.includes("umit") || name.includes("ates") || name.includes("ateş");
}

// Role icon avatar component
function RoleIconAvatar({ role, size = "lg" }) {
  const { Icon, gradient, bg, color } = getRoleIcon(role);
  const dim = size === "lg" ? "h-24 w-24 sm:h-28 sm:w-28" : "h-20 w-20";
  const iconDim = size === "lg" ? "h-11 w-11 sm:h-12 sm:w-12" : "h-9 w-9";
  return (
    <div className={`${dim} rounded-2xl flex items-center justify-center relative overflow-hidden ${bg} border border-mist shadow-sm`}>
      {/* Subtle gradient ring */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-10 rounded-2xl`} />
      <Icon className={`${iconDim} ${color} relative z-10`} strokeWidth={1.5} />
    </div>
  );
}

export default function TeamGrid() {
  const { t } = useLanguage();
  const dynamicTeam = useTeam();
  const hasDynamic = Array.isArray(dynamicTeam) && dynamicTeam.length > 0;
  const teamList = hasDynamic ? dynamicTeam : STATIC_TEAM_MEMBERS;

  return (
    <section data-testid="about-team-section" className="bg-cream py-section sm:py-section-lg border-t border-mist">
      <div className="container-page">
        <Reveal as="div" className="max-w-2xl">
          <span className="eyebrow">
            <Sparkles className="h-3 w-3 text-brand-pink" />
            {t("about.team_eyebrow")}
          </span>
          <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
            {t("about.team_title_pre")}{" "}
            <span className="gold-foil-text">{t("about.team_title_accent")}</span>
          </h2>
        </Reveal>

        {/* Featured founder card */}
        <Reveal as="div" className="mt-14" delay={0.1}>
          <div
            data-testid="team-founder-card"
            className="relative grid gap-8 lg:grid-cols-12 rounded-3xl overflow-hidden
                       bg-gradient-to-br from-cream to-mist/60 border border-mist
                       p-7 sm:p-10 lg:p-12"
          >
            <div className="pointer-events-none absolute -top-20 -end-20 h-64 w-64 rounded-full opacity-50"
              style={{ background: "radial-gradient(circle at 30% 30%, #FF6B9D 0%, #EE4F7A 60%, transparent 100%)", filter: "blur(40px)" }}
            />

            <div className="lg:col-span-5 relative">
              {/* Founder icon avatar — large */}
              <div
                className="relative mx-auto lg:mx-0 h-44 w-44 sm:h-52 sm:w-52 rounded-3xl overflow-hidden
                           shadow-[0_28px_70px_-20px_rgba(238,79,122,0.35)] bg-brand-pink/5 border border-brand-pink/20
                           flex items-center justify-center"
                data-testid="founder-avatar"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-pink/15 to-brand-magenta/10" />
                <Rocket className="h-20 w-20 text-brand-pink relative z-10" strokeWidth={1.2} />
                <span className="absolute bottom-3 end-3 rounded-full bg-cream/95 backdrop-blur border border-mist px-3 py-1 text-[0.6rem] uppercase tracking-[0.18em] font-bold text-brand-pink">
                  Founder
                </span>
              </div>
            </div>

            <div className="lg:col-span-7 relative">
              <span className="text-micro uppercase text-brand-pink-deep">
                {t("about.team.founder_role")}
              </span>
              <h3 className="mt-3 font-display text-display-2 font-bold text-ink leading-tight">
                {t("about.team.founder_name")}
              </h3>
              <p className="mt-5 text-body-lg text-ink/75 leading-relaxed max-w-xl">
                {t("about.team.founder_bio")}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href="https://www.linkedin.com/in/umitatesua/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-ink/80 transition-colors"
                  data-testid="founder-linkedin"
                >
                  <Linkedin className="h-4 w-4" />
                  {t("about.team.founder_cta")}
                </a>
                <a
                  href="mailto:merhaba@yastikmedya.com"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-ink/20 px-5 py-2.5 text-sm text-ink hover:border-ink transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  merhaba@yastikmedya.com
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Team cards */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {teamList.map((m, i) => {
            const founder = isFounder(m);
            if (founder) return null; // founder already shown above
            const { gradient, color } = getRoleIcon(m.role);
            return (
              <Reveal
                as="div"
                key={m.id || `${m.name}-${i}`}
                delay={i * 0.08}
                className="rounded-2xl border border-mist bg-white p-6 sm:p-7 text-center hover:border-brand-pink/30 hover:shadow-card transition-[border-color,box-shadow] duration-300"
                data-testid={`team-card-${m.id || i + 1}`}
              >
                {/* Role icon */}
                <div className="flex justify-center mb-4">
                  <RoleIconAvatar role={m.role} size="md" />
                </div>
                <h3 className="font-display text-h3 font-semibold text-ink">{m.name}</h3>
                <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] bg-gradient-to-r ${gradient} text-white`}>
                  {m.role}
                </span>
                {m.bio && <p className="mt-3 text-xs text-ink/60 line-clamp-3">{m.bio}</p>}
              </Reveal>
            );
          })}

          {/* Join CTA */}
          <Reveal as="div" delay={0.32}>
            <a
              href="mailto:merhaba@yastikmedya.com?subject=Yastık%20Medya%20-%20Pozisyon%20Başvurusu"
              data-testid="team-join-cta"
              className="group h-full flex flex-col items-center justify-center text-center
                         rounded-2xl border-2 border-dashed border-brand-pink/40 bg-brand-pink/5
                         p-6 sm:p-7 transition-[border-color,background-color] duration-300
                         hover:border-brand-pink hover:bg-brand-pink/10"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-pink text-cream mb-3">
                <ArrowUpRight className="h-5 w-5" />
              </span>
              <h3 className="font-display text-h3 font-semibold text-ink">
                {t("about.team.join_cta_title")}
              </h3>
              <p className="mt-1 text-xs text-ink/55">{t("about.team.join_cta_desc")}</p>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
